import React, {useMemo, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faDownload, faSpinner} from '@fortawesome/pro-regular-svg-icons';

import {
  apiPerformCompanyCreditCheck,
  apiRunItcTraderApplication,
} from 'constants/Api';
import {exportFdApplicationToExcel} from '../ApplicationTabs/utils/exportFdApplicationToExcel';
import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import {ApplicationApprovedCashflowCard} from './components/ApplicationApprovedCashflowCard';
import {useSupplierApplicationJourney} from './SupplierApplicationJourneyContext';

type DashboardMetricKey =
  | 'completion'
  | 'funding-ask'
  | 'credit-score'
  | 'basic-farm-info'
  | 'production-plan'
  | 'outstanding-docs'
  | 'short-term-history'
  | 'mini-pl';

type IHistorySummary = {
  hectares: number | null;
  yieldPerHa: number | null;
};

function lshFormatText(value: unknown, fallback = 'Not provided'): string {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
}

function lshToNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function lshFormatCount(value: unknown): string {
  const numeric = lshToNumber(value);
  return numeric.toLocaleString('en-ZA', {
    maximumFractionDigits: numeric % 1 === 0 ? 0 : 2,
  });
}

function lshFormatCurrency(value: unknown): string {
  const amount = lshToNumber(value);
  return `R${new Intl.NumberFormat('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

function lshCreditToneClass(score: number | null | undefined): string {
  const value = Number(score);
  if (!Number.isFinite(value) || value < 0) return 'is-unknown';
  if (value >= 700) return 'is-good';
  if (value >= 600) return 'is-medium';
  return 'is-low';
}

function lshSummariseHistoryYear(
  records: Array<{
    numberOfHectaresFarmed?: number;
    yieldPerHectare?: number;
  }>,
): IHistorySummary {
  if (records.length === 0) {
    return {
      hectares: null,
      yieldPerHa: null,
    };
  }

  const totalHectares = records.reduce(
    (sum, record) => sum + lshToNumber(record?.numberOfHectaresFarmed),
    0,
  );

  if (totalHectares <= 0) {
    const yieldValues = records
      .map((record) => lshToNumber(record?.yieldPerHectare))
      .filter((value) => value > 0);
    const meanYield =
      yieldValues.length > 0
        ? yieldValues.reduce((sum, value) => sum + value, 0) /
          yieldValues.length
        : null;

    return {
      hectares: null,
      yieldPerHa: meanYield,
    };
  }

  const weightedYield = records.reduce((sum, record) => {
    const yieldPerHa = lshToNumber(record?.yieldPerHectare);
    const hectares = lshToNumber(record?.numberOfHectaresFarmed);
    return sum + yieldPerHa * hectares;
  }, 0);

  return {
    hectares: totalHectares,
    yieldPerHa: weightedYield / totalHectares,
  };
}

function lshGetRecentHistoryYears(count = 3): number[] {
  const currentYear = new Date().getFullYear();
  return Array.from(
    {length: count},
    (_, index) => currentYear - (index + 1),
  ).sort((left, right) => left - right);
}

function lshSplitRegistrationNumber(registrationNumber: string) {
  const cleaned = registrationNumber.replace(/\D/g, '');
  if (cleaned.length < 8 || cleaned.length > 14) return null;

  const regNumber1 = cleaned.slice(0, 4);
  const regNumber3 = cleaned.slice(-2);
  const middleRaw = cleaned.slice(4, -2);
  const regNumber2 = middleRaw.padStart(6, '0').slice(-6);

  return {regNumber1, regNumber2, regNumber3};
}

function lshDownloadFileByUrl(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function lshGetSafeFileToken(value: string): string {
  const safe = value.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
  return safe || 'report';
}

function lshFormatMissingLabel(value: string): string {
  const normalised = value.replace(/[_-]+/g, ' ').trim().toLowerCase();

  if (!normalised) return 'Untitled requirement';

  return normalised.replace(/\b\w/g, (char) => char.toUpperCase());
}

const MINI_PL_ROWS: Array<{
  key: string;
  label: string;
  getValue: (
    record:
      | {
          revenue?: number;
          expenses?: number;
          operatingCosts?: number;
          profit?: number;
        }
      | undefined,
  ) => unknown;
}> = [
  {
    key: 'revenue',
    label: 'Revenue',
    getValue: (record) => record?.revenue,
  },
  {
    key: 'cos',
    label: 'COS',
    getValue: (record) => record?.expenses,
  },
  {
    key: 'opex',
    label: 'Opex',
    getValue: (record) => record?.operatingCosts,
  },
  {
    key: 'profit',
    label: 'Profit',
    getValue: (record) => record?.profit,
  },
];

const ApplicationDashboardStep: React.FC = () => {
  const {
    application,
    traderApplication,
    completion,
    applicantSummary,
    companySummary,
    farmSummary,
    financialSummary,
    documentsSummary,
    goNext,
    goToStep,
    refreshJourney,
  } = useSupplierApplicationJourney();

  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [
    isDownloadingIndividualCreditReport,
    setIsDownloadingIndividualCreditReport,
  ] = useState(false);
  const [
    isDownloadingCompanyCreditReport,
    setIsDownloadingCompanyCreditReport,
  ] = useState(false);

  const productionPlanRows = useMemo(() => {
    return [...(application?.productionPlans || [])].sort((left, right) => {
      const nameA = left?.crop?.name ?? '';
      const nameB = right?.crop?.name ?? '';
      return nameA.localeCompare(nameB);
    });
  }, [application?.productionPlans]);

  const historyYears = useMemo(() => lshGetRecentHistoryYears(3), []);

  const historyByCrop = useMemo(() => {
    const grouped = new Map<
      string,
      {
        cropName: string;
        recordsByYear: Map<number, Array<any>>;
      }
    >();

    (application?.productionHistory || []).forEach((record) => {
      const cropName = lshFormatText(record?.crop?.name, 'Unknown crop');
      const cropId = record?.crop?.id;
      const key =
        cropId !== null && cropId !== undefined
          ? `crop:${String(cropId)}`
          : `name:${cropName.toLowerCase()}`;
      const year = Number(record?.year);

      if (!grouped.has(key)) {
        grouped.set(key, {
          cropName,
          recordsByYear: new Map<number, Array<any>>(),
        });
      }

      if (!Number.isFinite(year) || !historyYears.includes(year)) return;

      const group = grouped.get(key);
      if (!group) return;
      const records = group.recordsByYear.get(year) || [];
      records.push(record);
      group.recordsByYear.set(year, records);
    });

    return Array.from(grouped.entries())
      .sort((left, right) => left[1].cropName.localeCompare(right[1].cropName))
      .map(([key, group]) => {
        const byYear = new Map<number, IHistorySummary>();
        historyYears.forEach((year) => {
          byYear.set(
            year,
            lshSummariseHistoryYear(group.recordsByYear.get(year) || []),
          );
        });

        return {
          key,
          cropName: group.cropName,
          byYear,
        };
      });
  }, [application?.productionHistory, historyYears]);

  const financialByYear = useMemo(() => {
    const map = new Map<
      number,
      {
        year?: number;
        revenue?: number;
        expenses?: number;
        operatingCosts?: number;
        profit?: number;
      }
    >();

    (application?.financialPerformance || []).forEach((record) => {
      const year = Number(record?.year);
      if (!Number.isFinite(year)) return;
      map.set(year, record);
    });

    return map;
  }, [application?.financialPerformance]);

  const basicFarmInfo = useMemo(() => {
    return {
      companyName: lshFormatText(companySummary.displayName),
      farmName: lshFormatText(farmSummary.name),
      registrationNumber: lshFormatText(companySummary.registrationNumber),
      applicantName: lshFormatText(applicantSummary.fullName),
      phoneNumber: lshFormatText(applicantSummary.phoneNumber),
      email: lshFormatText(applicantSummary.email),
    };
  }, [applicantSummary, companySummary, farmSummary]);

  const outstandingDocs = useMemo(() => {
    return documentsSummary.incompleteDocumentTypes.map((item, index) => ({
      id: `${item}-${index}`,
      name: lshFormatMissingLabel(item),
    }));
  }, [documentsSummary.incompleteDocumentTypes]);

  const outstandingPreview = useMemo(
    () => outstandingDocs.slice(0, 4),
    [outstandingDocs],
  );

  const canRequestIndividualCreditReport =
    applicantSummary.fullName.length > 0 &&
    applicantSummary.idNumber.length > 0 &&
    applicantSummary.fullName.includes(' ');

  const companyRegistrationParts = useMemo(
    () => lshSplitRegistrationNumber(companySummary.registrationNumber),
    [companySummary.registrationNumber],
  );

  const handleDownloadToExcel = async () => {
    if (!application || isDownloadingExcel) return;

    setIsDownloadingExcel(true);
    try {
      await exportFdApplicationToExcel(application, traderApplication);
    } catch (error) {
      console.error(
        '[ApplicationDashboardStep] Failed to export funding application',
        error,
      );
      window.alert('Unable to download Excel right now. Please try again.');
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleDownloadIndividualCreditReport = async () => {
    if (isDownloadingIndividualCreditReport) return;
    if (!canRequestIndividualCreditReport || !applicantSummary.idNumber) {
      window.alert(
        'Missing applicant details. Please capture the applicant name, surname and ID number first.',
      );
      return;
    }

    const [name, ...surnameParts] = applicantSummary.fullName.split(' ');
    const surname = surnameParts.join(' ').trim();
    if (!name || !surname) {
      window.alert(
        'Missing applicant details. Please capture the applicant name and surname first.',
      );
      return;
    }

    setIsDownloadingIndividualCreditReport(true);
    try {
      const report = await apiRunItcTraderApplication({
        name,
        surname,
        idNumber: applicantSummary.idNumber,
        externalServiceRequest: true,
      });
      const reportUrl = String(report?.finalDocLink ?? '').trim();

      if (!reportUrl) {
        throw new Error('No individual credit report link returned.');
      }

      const fileToken = lshGetSafeFileToken(`${surname}_${name}`);
      lshDownloadFileByUrl(reportUrl, `Khula_ITC_${fileToken}.pdf`);
    } catch (error) {
      console.error(
        '[ApplicationDashboardStep] Failed to download individual credit report',
        error,
      );
      window.alert(
        'Unable to download individual credit report. Please try again.',
      );
    } finally {
      setIsDownloadingIndividualCreditReport(false);
    }
  };

  const handleDownloadCompanyCreditReport = async () => {
    if (isDownloadingCompanyCreditReport) return;
    if (!companyRegistrationParts) {
      window.alert(
        'Missing or invalid company registration number. Please update company details first.',
      );
      return;
    }

    setIsDownloadingCompanyCreditReport(true);
    try {
      const report = await apiPerformCompanyCreditCheck({
        ...companyRegistrationParts,
        refresh: false,
      });
      const reportUrl = String(report?.finalDocLink ?? '').trim();

      if (!reportUrl) {
        throw new Error('No company credit report link returned.');
      }

      const fileToken = lshGetSafeFileToken(companySummary.registrationNumber);
      lshDownloadFileByUrl(reportUrl, `Khula_Company_ITC_${fileToken}.pdf`);
    } catch (error) {
      console.error(
        '[ApplicationDashboardStep] Failed to download company credit report',
        error,
      );
      window.alert(
        'Unable to download company credit report. Please try again.',
      );
    } finally {
      setIsDownloadingCompanyCreditReport(false);
    }
  };

  const dashboardSections: DashboardMetricKey[] = [
    'completion',
    'funding-ask',
    'credit-score',
    'basic-farm-info',
    'production-plan',
    'outstanding-docs',
    'short-term-history',
    'mini-pl',
  ];

  return (
    <ApplicationJourneyStepLayout
      title="Application dashboard"
      description={
        'Track progress, key financials, production summaries, credit score and outstanding items in one place.'
      }
      secondaryAction={{
        label: 'Download to Excel',
        onClick: () => {
          void handleDownloadToExcel();
        },
        disabled: !application?.id || isDownloadingExcel,
        loading: isDownloadingExcel,
      }}
      primaryAction={{
        label: 'Continue to company details',
        onClick: () => goNext(),
      }}
      showFooterActions={false}
    >
      <section
        className="application-summary-dashboard application-summary-dashboard--page"
        aria-label="Application summary"
      >
        <div className="application-summary-dashboard__header">
          <p className="application-summary-dashboard__eyebrow">
            Summary dashboard
          </p>
        </div>

        {application ? (
          <ApplicationApprovedCashflowCard
            application={application}
            nominatedWalletId={
              application.applicationWallet
                ? String(application.applicationWallet)
                : undefined
            }
            onLinked={() => {
              void refreshJourney();
            }}
          />
        ) : null}

        {dashboardSections.map((sectionKey) => {
          if (sectionKey === 'completion') {
            return (
              <article
                key={sectionKey}
                className="application-summary-card application-summary-card--quarter"
              >
                <header>
                  <h4>Completion</h4>
                </header>
                <p className="application-summary-card__metric">
                  {completion}%
                </p>
                <p className="application-summary-card__meta">
                  Application completion
                </p>
                <div
                  className="application-summary-dashboard__bar"
                  aria-hidden="true"
                >
                  <span style={{width: `${completion}%`}} />
                </div>
              </article>
            );
          }

          if (sectionKey === 'funding-ask') {
            return (
              <article
                key={sectionKey}
                className="application-summary-card application-summary-card--quarter"
              >
                <header>
                  <h4>Funding ask</h4>
                  <button
                    type="button"
                    className="application-summary-card__link"
                    onClick={() =>
                      goToStep('financials', {subStepKey: 'funding-request'})
                    }
                  >
                    Edit
                  </button>
                </header>
                <p className="application-summary-card__metric">
                  {lshFormatCurrency(financialSummary.fundingAskTotal)}
                </p>
                <p className="application-summary-card__meta">
                  Expected: Not set
                </p>
              </article>
            );
          }

          if (sectionKey === 'credit-score') {
            return (
              <article
                key={sectionKey}
                className="application-summary-card application-summary-card--quarter"
              >
                <header>
                  <h4>Credit score</h4>
                  <button
                    type="button"
                    className="application-summary-card__link"
                    onClick={() => goToStep('credit-check')}
                  >
                    Open
                  </button>
                </header>
                <div className="application-summary-card__credit-score-row">
                  <p
                    className={`application-summary-card__score ${lshCreditToneClass(
                      applicantSummary.creditScore,
                    )}`}
                  >
                    {Number.isFinite(Number(applicantSummary.creditScore)) &&
                    Number(applicantSummary.creditScore) >= 0
                      ? Number(applicantSummary.creditScore)
                      : 'Not available'}
                  </p>
                  <button
                    type="button"
                    className="application-summary-card__icon-button"
                    onClick={() => {
                      void handleDownloadIndividualCreditReport();
                    }}
                    disabled={
                      !canRequestIndividualCreditReport ||
                      isDownloadingIndividualCreditReport
                    }
                    aria-label="Download individual credit report"
                    title="Download individual credit report"
                  >
                    <FontAwesomeIcon
                      icon={
                        isDownloadingIndividualCreditReport
                          ? faSpinner
                          : faDownload
                      }
                      spin={isDownloadingIndividualCreditReport}
                    />
                  </button>
                </div>
                <div className="application-summary-card__credit-score-row">
                  <p className="application-summary-card__meta">
                    Company credit report
                  </p>
                  <button
                    type="button"
                    className="application-summary-card__icon-button"
                    onClick={() => {
                      void handleDownloadCompanyCreditReport();
                    }}
                    disabled={
                      !companyRegistrationParts ||
                      isDownloadingCompanyCreditReport
                    }
                    aria-label="Download company credit report"
                    title="Download company credit report"
                  >
                    <FontAwesomeIcon
                      icon={
                        isDownloadingCompanyCreditReport
                          ? faSpinner
                          : faDownload
                      }
                      spin={isDownloadingCompanyCreditReport}
                    />
                  </button>
                </div>
              </article>
            );
          }

          if (sectionKey === 'basic-farm-info') {
            return (
              <article
                key={sectionKey}
                className="application-summary-card application-summary-card--quarter"
              >
                <header>
                  <h4>Basic farm info</h4>
                  <button
                    type="button"
                    className="application-summary-card__link"
                    onClick={() => goToStep('company-details')}
                  >
                    Open
                  </button>
                </header>
                <dl className="application-summary-card__details">
                  <dt>Company name</dt>
                  <dd>{basicFarmInfo.companyName}</dd>
                  <dt>Farm name</dt>
                  <dd>{basicFarmInfo.farmName}</dd>
                  <dt>Reg number</dt>
                  <dd>{basicFarmInfo.registrationNumber}</dd>
                  <dt>Applicant</dt>
                  <dd>{basicFarmInfo.applicantName}</dd>
                  <dt>Phone</dt>
                  <dd>{basicFarmInfo.phoneNumber}</dd>
                  <dt>Email</dt>
                  <dd>{basicFarmInfo.email}</dd>
                </dl>
              </article>
            );
          }

          if (sectionKey === 'production-plan') {
            return (
              <article key={sectionKey} className="application-summary-card">
                <header>
                  <h4>Production plan</h4>
                  <button
                    type="button"
                    className="application-summary-card__link"
                    onClick={() =>
                      goToStep('production-info', {
                        subStepKey: 'production-plan',
                      })
                    }
                  >
                    Edit
                  </button>
                </header>
                {productionPlanRows.length > 0 ? (
                  <table className="application-summary-table">
                    <thead>
                      <tr>
                        <th scope="col">Crop</th>
                        <th scope="col">Ha</th>
                        <th scope="col">Yield/ha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productionPlanRows.map((plan) => (
                        <tr key={plan.id}>
                          <td>{plan?.crop?.name ?? 'Crop'}</td>
                          <td>{lshFormatCount(plan.numberOfHectares)}</td>
                          <td>{lshFormatCount(plan.estimatedYield)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="application-summary-card__empty">
                    No crops captured yet.
                  </p>
                )}
              </article>
            );
          }

          if (sectionKey === 'outstanding-docs') {
            return (
              <article key={sectionKey} className="application-summary-card">
                <header>
                  <h4>Outstanding docs</h4>
                  <button
                    type="button"
                    className="application-summary-card__link"
                    onClick={() => goToStep('upload-documents')}
                  >
                    Open
                  </button>
                </header>
                {outstandingDocs.length > 0 ? (
                  <>
                    <p className="application-summary-card__meta">
                      {outstandingDocs.length} required document
                      {outstandingDocs.length === 1 ? '' : 's'} outstanding
                    </p>
                    <ul className="application-summary-card__list">
                      {outstandingPreview.map((requirement) => (
                        <li key={requirement.id}>{requirement.name}</li>
                      ))}
                    </ul>
                    {outstandingDocs.length > outstandingPreview.length ? (
                      <p className="application-summary-card__meta">
                        +{outstandingDocs.length - outstandingPreview.length}{' '}
                        more
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="application-summary-card__meta">
                    All required docs uploaded.
                  </p>
                )}
              </article>
            );
          }

          if (sectionKey === 'short-term-history') {
            return (
              <article key={sectionKey} className="application-summary-card">
                <header>
                  <h4>Short-term history</h4>
                  <button
                    type="button"
                    className="application-summary-card__link"
                    onClick={() =>
                      goToStep('production-info', {
                        subStepKey: 'short-term-production-history',
                      })
                    }
                  >
                    Open
                  </button>
                </header>
                {historyByCrop.length > 0 ? (
                  <div className="application-summary-table-wrap">
                    <table className="application-summary-table application-summary-table--history-matrix">
                      <thead>
                        <tr>
                          <th scope="col" rowSpan={2}>
                            Crop
                          </th>
                          {historyYears.map((year) => (
                            <th
                              key={`history-year-${year}`}
                              scope="col"
                              colSpan={2}
                            >
                              {year}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          {historyYears.map((year) => (
                            <React.Fragment key={`history-cols-${year}`}>
                              <th scope="col">Ha</th>
                              <th scope="col">Yield</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {historyByCrop.map((row) => (
                          <tr key={row.key}>
                            <td>{row.cropName}</td>
                            {historyYears.map((year) => {
                              const summary = row.byYear.get(year);
                              return (
                                <React.Fragment key={`${row.key}-${year}`}>
                                  <td>
                                    {summary?.hectares === null
                                      ? '—'
                                      : lshFormatCount(summary?.hectares)}
                                  </td>
                                  <td>
                                    {summary?.yieldPerHa === null
                                      ? '—'
                                      : lshFormatCount(summary?.yieldPerHa)}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="application-summary-card__empty">
                    No short-term history captured yet.
                  </p>
                )}
              </article>
            );
          }

          return (
            <article key={sectionKey} className="application-summary-card">
              <header>
                <h4>Mini P&amp;L (3 years)</h4>
                <button
                  type="button"
                  className="application-summary-card__link"
                  onClick={() =>
                    goToStep('financials', {
                      subStepKey: 'financial-summary',
                    })
                  }
                >
                  Edit
                </button>
              </header>
              <div className="application-summary-table-wrap">
                <table className="application-summary-table application-summary-table--pl-matrix">
                  <thead>
                    <tr>
                      <th scope="col">Metric</th>
                      {historyYears.map((year) => (
                        <th key={`pl-year-${year}`} scope="col">
                          {year}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MINI_PL_ROWS.map((metric) => (
                      <tr key={metric.key}>
                        <td>{metric.label}</td>
                        {historyYears.map((year) => {
                          const record = financialByYear.get(year);
                          return (
                            <td key={`${metric.key}-${year}`}>
                              {lshFormatCurrency(metric.getValue(record))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          );
        })}
      </section>
    </ApplicationJourneyStepLayout>
  );
};

export default ApplicationDashboardStep;
